import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Fade,
  styled,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  Pagination,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  LocationOn as LocationIcon,
  Assignment as AssignmentIcon,
  LocalShipping as LocalShippingIcon,
  AttachMoney as AttachMoneyIcon,
  Dashboard as DashboardIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { BusinessForm } from './components/BusinessForm';
import { BusinessListingResult } from '../../data/types/application';
import { ListingStatus, MinimumOrderType } from '../../data/types/domain';
import { PageGradient } from '../../components/PageGradient';
import { alpha } from '@mui/material/styles';
import { businessListingQueries } from '../../domain/queries/businessListingQueries';
import { CompanyCard, CompanyLogo, CompanyDetails } from '../comparison-page/components/CompanyCardComponents';
import { capitalize } from '../../domain/utils/common';
import { useNavigate } from 'react-router-dom';
import BusinessRating from '../../components/BusinessRating';
import { deleteBusinessListing } from '../../domain/usecases/usecases';

// Create a styled container like the comparison page
const StyledContainer = styled(Container)(({ theme }) => ({
  maxWidth: '1200px',
  padding: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

// Additional styled components for the business listing
const PolicyBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.5, 1),
  fontSize: '0.875rem',
  border: `1px solid ${theme.palette.divider}`,
}));

const RatingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 1),
  fontSize: '0.875rem',
  fontWeight: 500,
  borderRadius: theme.shape.borderRadius,
}));

// Add a new styled component for the action bar
const ActionBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 3),
  backgroundColor: theme.palette.mode === 'dark'
    ? alpha(theme.palette.background.default, 0.6)
    : alpha(theme.palette.grey[50], 0.7),
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottomLeftRadius: theme.shape.borderRadius,
  borderBottomRightRadius: theme.shape.borderRadius,
  marginTop: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5, 2),
  },
}));

// Add new styled components
const SearchToolbar = styled(Paper)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.mode === 'dark'
    ? alpha(theme.palette.background.paper, 0.8)
    : alpha(theme.palette.grey[50], 0.9),
  backdropFilter: 'blur(8px)',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 700,
  background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
  },
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  height: 48,
  padding: theme.spacing(0.75, 2),
  borderRadius: theme.shape.borderRadius,
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.875rem',
  transition: 'all 0.2s ease',
  boxShadow: theme.shadows[1],
  whiteSpace: 'nowrap',
  minWidth: '100px',
  display: 'flex',
  alignItems: 'center',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: theme.shadows[2],
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    height: 32,
    fontSize: '0.8rem',
    minWidth: 'unset',
  },
}));

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row-reverse',
  gap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    width: '100%',
  },
}));

export const BusinessListPage: React.FC = () => {
  const [businesses, setBusinesses] = useState<BusinessListingResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState<BusinessListingResult | undefined>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [businessToDelete, setBusinessToDelete] = useState<BusinessListingResult | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const fetchBusinesses = async (ignoreLoading = false) => {
    try {
      if (!ignoreLoading) {
        setLoading(true);
        setError(null);
      }
      const data = await businessListingQueries.getBusinessListings({
        page: currentPage,
        pageSize,
        ignoreInactive: false,
      });
      setBusinesses(data.data);
      setTotalPages(data.totalPages);
      setTotalCount(data.totalCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch businesses');
    } finally {
      if (!ignoreLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchBusinesses();
  }, [currentPage]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleSearchSubmit = async () => {
    try {
      setLoading(true);
      const data = await businessListingQueries.filterBusinessListings({
        page: 1,
        pageSize,
        searchQuery,
        ignoreInactive: false,
      });
      setBusinesses(data.data);
      setTotalPages(data.totalPages);
      setTotalCount(data.totalCount);
      setCurrentPage(1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search businesses');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingBusiness(undefined);
    setFormOpen(true);
  };

  const handleEdit = (businessListing: BusinessListingResult) => {
    setEditingBusiness(businessListing);
    setFormOpen(true);
  };

  const handleDelete = async (businessListing: BusinessListingResult) => {
    setBusinessToDelete(businessListing);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!businessToDelete?.business.id) return;

    try {
      await deleteBusinessListing({ business_id: businessToDelete.business.id });
      setBusinesses(businesses.filter(b => b.business.id !== businessToDelete.business.id));
      setDeleteDialogOpen(false);
      setBusinessToDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete business');
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setBusinessToDelete(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh', pb: 6 }}>
      <PageGradient />
      <StyledContainer>
        <PageTitle variant="h4">
          Business Listings
        </PageTitle>
        <PageSubtitle>
          Manage and organize your shipping business listings
        </PageSubtitle>

        <SearchToolbar>
          <TextField
            fullWidth
            placeholder="Search businesses..."
            value={searchQuery}
            onChange={handleSearch}
            onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              sx: {
                height: 48,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              },
            }}
          />
          <ActionButton
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            Add Listing
          </ActionButton>
        </SearchToolbar>

        {error && (
          <Fade in>
            <Alert
              severity="error"
              sx={{
                mb: { xs: 2, sm: 3, md: 4 },
                borderRadius: 2,
                boxShadow: theme.shadows[2],
              }}
            >
              {error}
            </Alert>
          </Fade>
        )}

        {/* Business Listings */}
        <Typography
          variant="body2"
          color="text.disabled"
          sx={{
            fontSize: '0.875rem',
            fontWeight: 500,
            letterSpacing: '0.01em',
            marginBottom: 2,
          }}
        >
          Showing {businesses.length} of {totalCount} shipping companies
        </Typography>
        <Box>
          {businesses.map((businessListing) => (
            <Fade in timeout={500} key={businessListing.business.id}>
              <CompanyCard sx={{
                mb: 3,
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                boxShadow: theme.shadows[2],
              }}>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr' },
                  gap: { xs: 2, sm: 3 },
                  mb: 3,
                }}>
                  {/* Logo Section */}
                  <CompanyLogo isDark={theme.palette.mode === 'dark'}>
                    {businessListing.business.logo ? (
                      <img
                        src={businessListing.business.logo}
                        alt={`${businessListing.business.name} logo`}
                      />
                    ) : (
                      <BusinessIcon sx={{
                        fontSize: { xs: 36, sm: 42 },
                        color: 'text.secondary',
                        opacity: 0.5,
                      }} />
                    )}
                  </CompanyLogo>

                  {/* Details Section */}
                  <CompanyDetails>
                    <Typography
                      variant="h6"
                      className="company-name"
                      sx={{
                        fontSize: { xs: '1.2rem', sm: '1.35rem' },
                        fontWeight: 600,
                        mb: 0.75,
                        color: 'text.primary'
                      }}
                    >
                      {businessListing.business.name}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
                      {businessListing.business.website && (
                        <Link
                          href={businessListing.business.website}
                          target="_blank"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: 'text.secondary',
                            textDecoration: 'none',
                            '&:hover': { color: 'primary.main' },
                            fontSize: '0.875rem',
                          }}
                        >
                          <LanguageIcon sx={{ fontSize: '1rem' }} />
                          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>Website</Typography>
                        </Link>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          {businessListing.business.address}, {businessListing.business.postcode}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography
                      variant="body2"
                      className="company-description"
                      sx={{
                        mb: 2,
                        fontSize: '0.925rem',
                        color: 'text.secondary',
                        lineHeight: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {businessListing.business.description}
                    </Typography>

                    {/* Review Ratings */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                      {businessListing.business.google_rating && (
                        <BusinessRating
                          rating={businessListing.business.google_rating}
                          reviewsCount={businessListing.business.google_reviews_count || 0}
                          reviewsUrl={businessListing.business.google_reviews_url}
                          source="google"
                        />
                      )}
                      {businessListing.business.trustpilot_rating && (
                        <BusinessRating
                          rating={businessListing.business.trustpilot_rating}
                          reviewsCount={businessListing.business.trustpilot_reviews_count || 0}
                          reviewsUrl={businessListing.business.trustpilot_reviews_url}
                          source="trustpilot"
                        />
                      )}
                    </Box>

                    {/* Policy Information */}
                    <Box sx={{
                      display: 'flex',
                      gap: 1.5,
                      flexWrap: 'wrap'
                    }}>
                      {businessListing.listingPolicy && (
                        <PolicyBadge>
                          <AssignmentIcon sx={{ fontSize: '1.2rem', color: 'primary.main' }} />
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            {capitalize(businessListing.listingPolicy.listing_type)}
                            <Typography
                              component="span"
                              sx={{
                                ml: 0.75,
                                px: 0.75,
                                py: 0.2,
                                borderRadius: 1,
                                bgcolor: businessListing.listingPolicy.listing_status === ListingStatus.ACTIVE ? 'success.main' : 'warning.main',
                                color: '#fff',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                              }}
                            >
                              {capitalize(businessListing.listingPolicy.listing_status)}
                            </Typography>
                          </Typography>
                        </PolicyBadge>
                      )}

                      {businessListing.shippingPolicies && businessListing.shippingPolicies.length > 0 && (
                        <PolicyBadge>
                          <LocalShippingIcon sx={{ fontSize: '1.2rem', color: 'primary.main' }} />
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            {businessListing.shippingPolicies.length} Shipping Routes
                          </Typography>
                        </PolicyBadge>
                      )}

                      {businessListing.serviceLocations && businessListing.serviceLocations.length > 0 && (
                        <PolicyBadge>
                          <LocationIcon sx={{ fontSize: '1.2rem', color: 'primary.main' }} />
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            {businessListing.serviceLocations.length} Service Areas
                          </Typography>
                        </PolicyBadge>
                      )}

                      {businessListing.shippingPolicies && businessListing.shippingPolicies.length > 0 && (
                        <PolicyBadge>
                          <AttachMoneyIcon sx={{ fontSize: '1.2rem', color: 'primary.main' }} />
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            Min Order: {businessListing.shippingPolicies[0].minimum_order_amount} {businessListing.shippingPolicies[0].currency}
                            <Typography
                              component="span"
                              sx={{
                                ml: 0.75,
                                px: 0.75,
                                py: 0.2,
                                borderRadius: 1,
                                bgcolor: 'grey.200',
                                color: 'text.primary',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                              }}
                            >
                              {businessListing.shippingPolicies[0].minimum_order_type === MinimumOrderType.AMOUNT ? 'Fixed' : 'Per KG'}
                            </Typography>
                          </Typography>
                        </PolicyBadge>
                      )}
                    </Box>
                  </CompanyDetails>
                </Box>
                <Box sx={{
                  width: '100%',
                  borderBottom: 1,
                  borderColor: 'divider'
                }} />
                {/* Action Buttons at the bottom */}
                <ActionButtonsContainer>
                  <ActionButton
                    onClick={() => handleEdit(businessListing)}
                    startIcon={<EditIcon />}
                    variant="outlined"
                    color="primary"
                  >
                    Edit
                  </ActionButton>
                  <ActionButton
                    onClick={() => handleDelete(businessListing)}
                    startIcon={<DeleteIcon />}
                    variant="outlined"
                    color="error"
                  >
                    Delete
                  </ActionButton>
                  <ActionButton
                    onClick={() => navigate(`/dashboard?business_id=${businessListing.business.id}`)}
                    startIcon={<DashboardIcon />}
                    variant="outlined"
                    color="info"
                  >
                    View Dashboard
                  </ActionButton>
                </ActionButtonsContainer>
              </CompanyCard>
            </Fade>
          ))}
        </Box>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 4,
            mb: 2
          }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              color="primary"
              size={isMobile ? "small" : "medium"}
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 1,
                },
              }}
            />
          </Box>
        )}

        {businesses.length === 0 && !error && (
          <Fade in>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: { xs: '200px', sm: '300px' },
                textAlign: 'center',
                p: { xs: 2, sm: 4 },
                borderRadius: 4,
                backgroundColor: 'background.paper',
                boxShadow: theme.shadows[1],
              }}
            >
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{
                  mb: 2,
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  fontWeight: 600,
                }}
              >
                No business listings found
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  mb: 3,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                }}
              >
                Create your first listing to get started
              </Typography>
              <ActionButton
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAdd}
              >
                Add Listing
              </ActionButton>
            </Box>
          </Fade>
        )}

        <BusinessForm
          open={formOpen}
          onClose={() => setFormOpen(false)}
          initialData={editingBusiness}
          title={editingBusiness ? 'Edit Listing' : 'Add Listing'}
          isEditMode={!!editingBusiness}
          onUpdate={() => {
            fetchBusinesses(true);
          }}
        />

        <Dialog
          open={deleteDialogOpen}
          onClose={handleCancelDelete}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            Delete Business Listing
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Are you sure you want to delete the business "{businessToDelete?.business.name}"? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} color="primary">
              Cancel
            </Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained" autoFocus>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </StyledContainer>
    </Box>
  );
}; 